/**
 * Test blueprints. A blueprint is a coded description of fields that a test
 * template is partly generated from; the template stays an ordinary test
 * template. A template created from a blueprint is a "method": it gets the
 * blueprint's base fields plus one block of fields per compound, and its
 * compounds are its calibration curves.
 *
 * Every generated field carries a `key`, stored as the attribute's
 * `managed_key`. Regeneration matches on it, so fields the blueprint does not
 * own are never touched, and changing a blueprint is a deliberate version bump
 * rather than a silent edit of existing methods.
 *
 * A compound's field codes start with the method's template code (`BA` gives
 * `ba_putrescine_mg_kg`), so two methods that measure the same compound never
 * share a result code. A method's code is unique and fixed.
 */
/** Every field a blueprint generates for a method, in order. */
export function blueprintFields(blueprint, codePrefix, compounds) {
    return [
        ...blueprint.baseFields,
        ...compounds.flatMap((c) => blueprint.compoundFields(codePrefix, c)),
    ];
}
/** The managed key of one of a compound's fields. */
export function compoundFieldKey(analyte, field) {
    return `compound/${analyte}/${field}`;
}
/** The code of one of a compound's fields in a method with this code prefix. */
export function compoundFieldCode(codePrefix, analyte, field) {
    return `${codePrefix}_${analyte}_${field}`;
}
/** The compound and field a managed key names; null for a base field. */
export function parseCompoundFieldKey(key) {
    const match = /^compound\/([^/]+)\/([^/]+)$/.exec(key);
    return match ? { analyte: match[1], field: match[2] } : null;
}
/**
 * The prefix a template's code gives its field codes: lowercase, with anything
 * but letters and digits as underscores ("BA" → "ba", "PS(M" → "ps_m"). Null
 * when the code yields nothing that starts with a letter.
 */
export function codePrefixOf(templateCode) {
    const prefix = templateCode
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, "_")
        .replace(/^_+|_+$/g, "");
    return /^[a-z][a-z0-9_]*$/.test(prefix) ? prefix : null;
}
//# sourceMappingURL=blueprint.js.map